import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, CheckCircleIcon, PlusIcon, MinusIcon, XIcon } from '@heroicons/react/outline';
import { clearCart, removeFromCart, addToCart, increaseQuantity, decreaseQuantity, applyCoupon, removeCoupon, updatePrices } from '../../store/slices/cartSlice';
import { settingsApi } from '../../services/api/settings.api';
import ReactDOM from 'react-dom';
import PageTitle from '../common/PageTitle';
import { coursesApi } from '../../services/api/courses.api';
import { ordersApi } from '../../services/api/orders.api';
import { bookService } from '../../services/bookService';
import { couponsApi } from '../../services/api/coupons.api';
import toastService from '../../services/toast';
import { saveCart } from '../../utils/cart';

const Modal = ({ onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return ReactDOM.createPortal(
    <>
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm"
      />
      <div 
        className="fixed z-50 inset-0"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md relative">
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const Cart = () => {
  const cart = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // تحديد نوع المنتجات في السلة مع التحقق من قيمة type
  // جلب أحدث الأسعار عند تحميل صفحة السلة أو تغيير العناصر
  useEffect(() => {
    const fetchLatestPrices = async () => {
      if (cart.items.length === 0) return;
      try {
        const priceMap = {}; // key = id_type
        const updatedItemsPromises = cart.items.map(async (item) => {
          let latestPrice = item.price;
          try {
            if (item.type === 'course') {
              const res = await coursesApi.getPublicCourseById(item.id);
              latestPrice = res.discounted_price ?? res.price;
            } else if (item.type === 'book') {
              const res = await bookService.getById(item.id);
              latestPrice = res.price;
            }
          } catch (err) {
            console.error('Error fetching price', item.id, err);
          }
          priceMap[`${item.id}_${item.type}`] = latestPrice;
          return { ...item, price: latestPrice };
        });

        const updatedItems = await Promise.all(updatedItemsPromises);
        const newTotal = updatedItems.reduce((sum, it) => sum + it.price * it.quantity, 0);

        // تحديث Redux
        dispatch(updatePrices(priceMap));

        // حفظ السلة بالأسعار الجديدة
        saveCart({
          ...cart,
          items: updatedItems,
          totalAmount: newTotal
        });
      } catch (e) {
        console.error('Failed to refresh prices', e);
      }
    };

    fetchLatestPrices();
  }, [cart.items.length]);

  const cartItemTypes = cart.items.map(item => item.type || 'unknown');
  
  const hasBooks = cart.items.some(item => item.type === 'book');
  const hasCourses = cart.items.some(item => item.type === 'course');
  const hasOnlyBooks = hasBooks && !hasCourses;
  const hasOnlyCourses = hasCourses && !hasBooks;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsApi.getPaymentSettings();
        setPaymentSettings({
          enablePaymentTerms: response.enablePaymentTerms || false,
          booksTerms: response.booksTerms || '',
          coursesTerms: response.coursesTerms || '',
          generalTerms: response.generalTerms || ''
        });
      } catch (error) {
        // خطأ في جلب إعدادات الدفع
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // حالة السلة وأنواع المنتجات فيها

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleCheckout = async () => {
    if (cart.items.length > 0) {
      try {
        // لو مجاني نعمل الطلب على طول
        if (cart.totalAmount === 0) {
          const orderData = {
            items: cart.items.map(item => ({
              id: item.id,
              type: item.type,
              quantity: item.quantity,
              price: item.price || 0
            })),
            totalAmount: cart.totalAmount,
            discount: cart.discount || 0,
            finalTotal: cart.totalAmount - (cart.discount || 0),
            paymentMethod: 'free'
          };
          
          // إضافة كود الكوبون إذا كان موجود
          if (cart.couponCode) {
            orderData.couponCode = cart.couponCode;
            orderData.couponDetails = cart.couponDetails;
          }
          
          // نعمل الطلب
          const response = await ordersApi.submitOrder(orderData);
          
          // نفضي السلة
          dispatch(clearCart());
          
          // نروح لصفحة تأكيد الطلب
          navigate(`/order-confirmation/${response.orderId}`);
        }
        // لو مدفوع نشوف الشروط الأول
        else if (paymentSettings?.enablePaymentTerms) {
          setShowTerms(true);
        } else {
          navigate('/checkout');
        }
      } catch (error) {
        console.error('Error during checkout:', error);
        toastService.error('حدث خطأ أثناء إتمام الطلب');
      }
    }
  };

  const handleAcceptTerms = () => {
    setShowTerms(false);
    navigate('/checkout');
  };

  const handleIncreaseQuantity = (item) => {
    dispatch(increaseQuantity(item));
  };

  const handleDecreaseQuantity = (item) => {
    if (item.quantity > 1) {
      dispatch(decreaseQuantity(item));
    } else {
      dispatch(removeFromCart(item));
    }
  };

  // دالة التحقق من الكوبون وتطبيقه
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await couponsApi.validateCoupon(couponCode, cart.items);
      
      if (response.valid) {
        // تطبيق الكوبون
        const couponData = {
          code: couponCode,
          discount: response.discount,
          details: response.couponDetails
        };
        
        dispatch(applyCoupon(couponData));
        
        // عرض رسالة بسيطة فقط
        toastService.success('تم تطبيق كوبون الخصم بنجاح');
      } else {
        toastService.error(response.message || 'الكوبون غير صالح');
        setCouponCode('');
      }
    } catch (error) {
      // خطأ في التحقق من الكوبون
      if (error.response && error.response.data && error.response.data.message) {
        toastService.error(error.response.data.message);
      } else {
        toastService.error('حدث خطأ أثناء التحقق من الكوبون');
      }
      setCouponCode('');
    } finally {
      setValidatingCoupon(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-16">
        <PageTitle title="السلة" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">السلة فارغة</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">لم تقم بإضافة أي منتجات إلى السلة بعد</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              تصفح المنتجات
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getTermsToShow = () => {
    if (!paymentSettings?.enablePaymentTerms) return [];
    
    // تحليل أنواع المنتجات في السلة
    
    // تحديد أنواع المنتجات في السلة بدقة
    const booksCount = cart.items.filter(item => item.type === 'book').length;
    const coursesCount = cart.items.filter(item => item.type === 'course').length;
    const hasBooks = booksCount > 0;
    const hasCourses = coursesCount > 0;
    
    // معلومات السلة وإعدادات الدفع
    
    let terms = [];
    
    // الحالة الأولى: السلة تحتوي على كتب ودورات معاً
    if (hasBooks && hasCourses) {
      // في حالة وجود كتب ودورات معاً، نعرض الشروط العامة فقط
      // عرض الشروط العامة للسلة المختلطة
      if (paymentSettings.generalTerms && paymentSettings.generalTerms.trim() !== '') {
        terms.push({ title: 'الشروط العامة', content: paymentSettings.generalTerms });
      }
    }
    // الحالة الثانية: السلة تحتوي على كتب فقط
    else if (hasBooks && !hasCourses) {
      // في حالة وجود كتب فقط، نعرض شروط الكتب
      // عرض شروط الكتب للسلة التي تحتوي على كتب فقط
      if (paymentSettings.booksTerms && paymentSettings.booksTerms.trim() !== '') {
        terms.push({ title: 'شروط شراء الكتب', content: paymentSettings.booksTerms });
      }
    }
    // الحالة الثالثة: السلة تحتوي على دورات فقط
    else if (!hasBooks && hasCourses) {
      // في حالة وجود دورات فقط، نعرض شروط الدورات
      // عرض شروط الدورات للسلة التي تحتوي على دورات فقط
      if (paymentSettings.coursesTerms && paymentSettings.coursesTerms.trim() !== '') {
        terms.push({ title: 'شروط الدورات التدريبية', content: paymentSettings.coursesTerms });
      }
    }
    // الشروط التي سيتم عرضها
    return terms;
  };

  return (
    <>
      <div className="min-h-screen pt-16">
        <PageTitle title="السلة" />
        <div className="max-w-6xl mx-auto px-2 sm:px-6 py-2">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Right Side - Cart Items */}
            <div className="lg:col-span-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">سلة المشتريات</h2>
                </div>

                <div className="p-4">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex items-start gap-4 py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                        className="h-24 w-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          {item.type === 'course' ? 'دورة:' : 'كتاب:'} {item.author}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => dispatch(removeFromCart(item))}
                              className="hidden sm:flex items-center text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                            >
                              <TrashIcon className="h-5 w-5 ml-1" />
                              <span className="text-sm">إزالة</span>
                            </button>
                            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-2">
                              <button
                                onClick={() => handleDecreaseQuantity(item)}
                                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              >
                                <MinusIcon className="h-4 w-4" />
                              </button>
                              <span className="text-gray-900 dark:text-white font-medium min-w-[1.5rem] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleIncreaseQuantity(item)}
                                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                            {Math.round(item.price)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Left Side - Summary & Checkout */}
            <div className="lg:col-span-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                {/* Summary */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>المجموع</span>
                    <span>{Math.round(cart.totalAmount)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span></span>
                  </div>
                  
                  {/* عرض الخصم فقط إذا كان موجوداً */}
                  {cart.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 dark:text-green-400 mb-2">
                      <span>الخصم</span>
                      <span>- {Math.round(cart.discount)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span></span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span>الإجمالي</span>
                    <span>{Math.round(cart.totalAmount - (cart.discount || 0))} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span></span>
                  </div>
                </div>

                {/* Coupon */}
                <div className="mb-6">
                  <div className="flex overflow-hidden">
                    {cart.couponCode ? (
                      <>
                        {/* عرض الكوبون المطبق مع تعطيل التعديل */}
                        <input
                          type="text"
                          value={cart.couponCode}
                          className="flex-1 px-3 py-2 text-sm rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                          disabled={true}
                          readOnly
                        />
                        <button 
                          onClick={() => {
                            dispatch(removeCoupon());
                            setCouponCode('');
                            toastService.info('تم إزالة كوبون الخصم');
                          }}
                          className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 border-l border-red-200 dark:border-red-800 rounded-l-md transition-colors"
                        >
                          إزالة
                        </button>
                      </>
                    ) : (
                      <>
                        {/* حقل إدخال الكوبون الجديد */}
                        <input
                          type="text"
                          placeholder="هل لديك قسيمة خصم؟"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          disabled={validatingCoupon}
                          className="flex-1 px-3 py-2 text-sm rounded-r-md border border-gray-300 dark:border-gray-600 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right w-32"
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode.trim()}
                          className="px-4 py-2 text-sm font-medium text-gray-800 hover:text-black dark:text-gray-100 dark:hover:text-white bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 border-l border-gray-300 dark:border-gray-600 rounded-l-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validatingCoupon ? 'جاري التحقق...' : 'إضافة'}
                        </button>
                      </>
                    )}
                  </div>
                  {cart.couponCode && (
                    <div className="mt-2 text-sm text-green-600 dark:text-green-400 text-center">
                      تم تطبيق كوبون الخصم بنجاح
                    </div>
                  )}
                </div>

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full mt-6 px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'جاري التحميل...' : cart.totalAmount === 0 ? 'شراء' : 'المتابعة للدفع'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && paymentSettings?.enablePaymentTerms && (
        <Modal onClose={() => setShowTerms(false)}>
          <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <CheckCircleIcon className="h-10 w-10 text-primary-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">الشروط والأحكام</h3>
              <div className="text-right mb-4">
                {getTermsToShow().length > 0 ? (
                  getTermsToShow().map((term, index) => (
                    <div key={index} className="mb-4 whitespace-pre-wrap text-gray-600 dark:text-gray-300">
                      <h4 className="text-lg font-bold mb-2">{term.title}</h4>
                      <div dangerouslySetInnerHTML={{ __html: (term.content || '').replace(/\n/g, '<br />') }} />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">لا توجد شروط للعرض</p>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowTerms(false);
                    navigate('/checkout');
                  }}
                  className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  موافق
                </button>
                <button
                  onClick={() => setShowTerms(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
export default Cart;
